import { DayCellContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import FullCalendar from '@fullcalendar/react';
import { Box } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { AppointmentDailyCount } from '@spike/appointments-list-model';
import { MarketplaceSchedule } from '@spike/model';
import get from 'lodash/get';
import { BusinessAreaType } from 'model/Service';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, createRef, useCallback, useEffect, useMemo } from 'react';
import { debugConsoleLog } from 'utils/GeneralUtils';
import { daysOfWeek } from '../FullCalendar/FullCalendarUtils';
import { useFullCalendarStyles } from '../FullCalendar/StylesFullCalendar';
import { UNASSIGNED_STAFF_ID } from '../types';
import MonthDayCellFullCalendar from './MonthDayCellFullCalendar';
import WeekdayHeaderFullCalendar from './WeekdayHeaderFullCalendar';

interface MonthFullCalendarProps {
    month: Moment;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    showPetsCountBadge: boolean;
    staffs: Array<Staff>;
    dailyCounts: Array<AppointmentDailyCount>;
    selectedStaffIds?: Array<number>;
    businessArea: BusinessAreaType;
    onBook?: (at: Moment) => void;
}

const getAppointmentsCount = (
    dailyCount: AppointmentDailyCount,
    selectedStaffIds: Array<number> | undefined
): number => {
    if (selectedStaffIds === undefined) {
        return dailyCount.totalCount;
    }

    const assignedCount = dailyCount.staffCounts.reduce((count, staffCount) => count + staffCount.total, 0);
    const unassignedCount = Math.max(dailyCount.totalCount - assignedCount, 0);

    return selectedStaffIds.reduce((count, staffId) => {
        if (staffId === UNASSIGNED_STAFF_ID) {
            return count + unassignedCount;
        }

        return count + (dailyCount.staffCounts.find(staffCount => staffCount.staffId === staffId)?.total ?? 0);
    }, 0);
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'width': '100%',
            '& .fc .fc-timegrid-slot-label': {
                border: 'none !important'
            },
            '& .fc-bg-event': {
                opacity: 1
            },
            '& .fc-daygrid-day-top': {
                flexDirection: 'row'
            },
            '& .fc-daygrid-day-number': {
                width: '100%'
            },
            '& .fc-daygrid-day-events': {
                minHeight: '0px !important'
            },
            '& .fc-day-today': {
                backgroundColor: 'white !important'
            }
        },
        dayHeader: {
            'backgroundColor': 'white',
            'borderTop': 'none !important',
            'borderLeft': 'none !important',
            'borderRight': 'none !important',
            'borderBottom': '1px solid #D8D8D8 !important',
            'verticalAlign': 'middle !important',
            [theme.breakpoints.down('sm')]: {
                height: '24px'
            },
            [theme.breakpoints.only('md')]: {
                height: '35px'
            },
            [theme.breakpoints.only('lg')]: {
                height: '43px'
            },
            [theme.breakpoints.only('xl')]: {
                height: '65px'
            },

            '& .fc-col-header-cell-cushion': {
                textDecoration: 'none'
            }
        }
    })
);

export const MonthFullCalendar: FunctionComponent<MonthFullCalendarProps> = props => {
    const classes = useStyles();
    const fullcalendarClasses = useFullCalendarStyles();

    const fullCalendarRef = createRef<FullCalendar>();
    const getCalendarNow = useCallback(() => moment.tz(props.timeZone).format(), [props.timeZone]);

    useEffect(() => {
        fullCalendarRef.current?.getApi().gotoDate(props.month.format('YYYY-MM'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.month]);

    const appointmentsByDate: Map<string, number> = useMemo(() => {
        return props.dailyCounts.reduce((map, dailyCount) => {
            const count = getAppointmentsCount(dailyCount, props.selectedStaffIds);

            if (count > 0) {
                map.set(dailyCount.date, count);
            }

            return map;
        }, new Map<string, number>());
    }, [props.dailyCounts, props.selectedStaffIds]);

    const dateClickHandler = (args: DateClickArg, timeZone: string) => {
        const selectedDate = moment(args.date).tz(timeZone);
        const at = moment.tz(selectedDate.format('YYYY-MM-DD'), timeZone);

        debugConsoleLog(`date: ${selectedDate.format('YYYY-MM-DD')} moment: ${at.format()}`);

        const dayOfWeek = at.format('dddd').toLocaleLowerCase();
        const businessHours = get(props.schedule?.week, dayOfWeek);
        const [hour, minutes] = businessHours?.open?.split(':') ?? ['8', '0'];
        at.hour(hour);
        at.minutes(minutes);

        props.onBook && props.onBook(at);
    };

    const renderMonthDayCell = (args: DayCellContentArg, appointmentsByDate: Map<string, number>) => (
        <MonthDayCellFullCalendar
            {...args}
            timeZone={props.timeZone}
            showPetsCountBadge={props.showPetsCountBadge}
            staffs={props.staffs}
            selectedStaffIds={props.selectedStaffIds}
            businessArea={props.businessArea}
            appointmentsCount={appointmentsByDate.get(moment(args.date).tz(props.timeZone).format('YYYY-MM-DD')) ?? 0}
        />
    );

    const openDaysOfWeek = useMemo(
        () =>
            Array.from(daysOfWeek.keys())
                .filter(weekDay => get(props.schedule?.week, weekDay) != null)
                .map(weekDay => daysOfWeek.get(weekDay)),
        [props.schedule?.week]
    );

    const specialDays = useMemo(
        () =>
            (props.schedule?.specialHours ?? []).map(specialHour => ({
                start: specialHour.date,
                end: specialHour.date,
                display: 'background',
                color: specialHour.isClosed ? '#f2f2f2' : 'white'
            })),
        [props.schedule?.specialHours]
    );

    const fullCalendarComponent = useMemo(
        () => (
            <FullCalendar
                ref={fullCalendarRef}
                plugins={[interactionPlugin, momentTimezonePlugin, dayGridPlugin]}
                initialView="dayGridMonth"
                timeZone={props.timeZone}
                now={getCalendarNow}
                editable={true}
                height="100%"
                headerToolbar={false}
                dayHeaderContent={props => <WeekdayHeaderFullCalendar {...props} />}
                dayHeaderClassNames={[classes.dayHeader]}
                dayCellClassNames={[fullcalendarClasses.dayCell]}
                dayCellContent={args => renderMonthDayCell(args, appointmentsByDate)}
                dateClick={args => dateClickHandler(args, props.timeZone)}
                firstDay={0}
                businessHours={{
                    daysOfWeek: openDaysOfWeek
                }}
                events={specialDays}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            appointmentsByDate,
            openDaysOfWeek,
            props.businessArea,
            props.month,
            props.selectedStaffIds,
            props.staffs,
            specialDays,
            props.timeZone,
            getCalendarNow
        ]
    );

    return <Box className={classes.container}>{fullCalendarComponent}</Box>;
};

export default MonthFullCalendar;
